import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  GlobalStyles,
  MenuItem,
  Paper,
  TextField,
  Theme,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Controller, useForm } from "react-hook-form";
import React, { useEffect, useRef } from "react";
import { ModalTransition } from "../../../common/components/transitions";
import StyledDialogTitle from "../../../common/components/dialog/dialog-title";
import StyledDialogActions from "../../../common/components/dialog/dialog-actions";
import {
  commonTextFieldProps,
  datePickerProps,
} from "../../../common/components/textfield";
import {
  AnnouncementCategory,
  announcementCategoryOptions,
} from "../../../common/enums/announcement-category";
import dayjs from "dayjs";
import useHomeStore from "../api/store";
import {
  AddAnnouncementSchema,
  addAnnouncementSchema,
  defaultValues,
} from "../api/schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCreateAnnouncement, useUpdateAnnouncement } from "../api/hooks";
import { ImageCropDialog } from "./banner-crop-dialog";
import { Handlers } from "../api/handlers";
import { useNavigate } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useAuthStore } from "../../../common/auth/store";
import { getAnnouncementPrivileges } from "../../../common/auth/privileges";
import { commonButtonProps } from "../../../common/components/buttons/sxprops";
interface Props {
  announcementId?: string;
  onSaved?: () => void;
}

export function AddAnnouncementModal({ announcementId, onSaved }: Props) {
  const {
    isAddModalOpen,
    setAddModal,
    previewUrl,
    previewUrl2,
    originalFileName,
    openCropDialog,
    setOpenCropDialog,
    setOriginalFileName,
    setPreviewUrl2,
    setPreviewUrl,
    selectedAnnouncement,
    setSelectedAnnouncement,
  } = useHomeStore();

  // react-hook-form setup
  const {
    control,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm<AddAnnouncementSchema>({
    resolver: zodResolver(addAnnouncementSchema),
    defaultValues,
    mode: "onChange",
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  const { mutate: createAnnouncement, isPending: isCreateLoading } =
    useCreateAnnouncement();
  const { mutate: updateAnnouncement, isPending: isUpdateLoading } =
    useUpdateAnnouncement();

  const navigate = useNavigate();
  const handlers = new Handlers(navigate);

  const authStore = useAuthStore();
  const anncouncementPrivileges = getAnnouncementPrivileges(
    authStore.privileges,
  );

  useEffect(() => {
    if (isAddModalOpen && selectedAnnouncement) {
      const catValue =
        typeof selectedAnnouncement.category === "number"
          ? selectedAnnouncement.category
          : categoryKeyOrLabelToNumber(selectedAnnouncement.category as string);
      reset({
        date:
          typeof selectedAnnouncement.date === "string"
            ? selectedAnnouncement.date
            : selectedAnnouncement.date instanceof Date
              ? selectedAnnouncement.date.toISOString()
              : undefined,
        category: catValue,
        title: selectedAnnouncement.title,
        body: selectedAnnouncement.body,
        announcementPhoto: selectedAnnouncement.photoUrl,
      });

      setPreviewUrl(selectedAnnouncement.photoUrl || null);
    }
  }, [isAddModalOpen, selectedAnnouncement, reset, setPreviewUrl]);

  function categoryKeyOrLabelToNumber(keyOrLabel: string): number | undefined {
    if ((AnnouncementCategory as any)[keyOrLabel] !== undefined) {
      return (AnnouncementCategory as any)[keyOrLabel] as number;
    }
    const found = announcementCategoryOptions.find(
      (opt) => opt.label === keyOrLabel,
    );
    return found ? found.value : undefined;
  }
  const handleClose = () => {
    setPreviewUrl(null);
    setPreviewUrl2(null);
    setOriginalFileName("");
    reset(defaultValues);

    setSelectedAnnouncement(null);

    setAddModal(false);
    handlers.handleModalClose();
  };

  const handleAddClick = () => {
    fileInputRef.current?.click();
  };
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    const file = files[0];
    if (
      file.type === "image/jpeg" ||
      file.type === "image/jpg" ||
      file.type === "image/png"
    ) {
      const url = URL.createObjectURL(file);
      setPreviewUrl2(url);
      setOriginalFileName(file.name);
      setOpenCropDialog(true);
      event.target.value = "";
    } else {
      console.warn("Only JPG, JPEG, or PNG files are allowed.");
    }
  };
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;
    const file = files[0];
    if (
      file.type === "image/jpeg" ||
      file.type === "image/jpg" ||
      file.type === "image/png"
    ) {
      const url = URL.createObjectURL(file);
      setPreviewUrl2(url);
      setOriginalFileName(file.name);
      setOpenCropDialog(true);
      e.dataTransfer.clearData();
    } else {
      console.warn("Only JPG, JPEG, or PNG files are allowed.");
    }
  };
  const COLOR_OPTIONS = [
    "",
    "#51a7f9",
    "#6fc040",
    "#f5d427",
    "#f3901d",
    "#ed5c57",
    "#b36ae2",

    "#0c64c0",
    "#0c882a",
    "#dcbe22",
    "#de6a19",
    "#c82613",
    "#763e9b",

    "#174e86",
    "#0f5c1a",
    "#c3971d",
    "#be5b17",
    "#861106",
    "#5e327c",

    "#002451",
    "#06400c",
    "#a37519",
    "#934511",
    "#570606",
    "#3b204d",

    "#ffffff",
    "#cccccc",
    "#999999",
    "#666666",
    "#333333",
    "#000000",
  ];

  const BG_OPTIONS = [
    "",

    "#00ffff",
    "#00ff00",
    "#ffff00",
    "#ff8000",
    "#ff0000",
    "#ff00ff",

    "#80ffff",
    "#80ff80",
    "#ffff80",
    "#ffc080",
    "#ff8080",
    "#ff80ff",

    "#ffffff",
    "#cccccc",
    "#999999",
    "#666666",
    "#333333",
    "#000000",
  ];

  const handleFormSubmit = (data: AddAnnouncementSchema) => {
    if (selectedAnnouncement) {
      const payload = {
        id: selectedAnnouncement.id,
        ...data,
      };

      updateAnnouncement(payload, {
        onSuccess: () => {
          handleClose();
          onSaved?.();
        },
      });
    } else {
      createAnnouncement(data, {
        onSuccess: () => {
          handleClose();
        },
      });
    }
  };

  useEffect(() => {
    if (isAddModalOpen) {
      window.history.pushState({ modalOpen: true }, "");
      const onPopState = (e: PopStateEvent) => {
        if (isAddModalOpen) {
          handleClose();
          navigate("/home");
        }
      };
      window.addEventListener("popstate", onPopState);
    }
  }, [isAddModalOpen]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog
        open={isAddModalOpen}
        onClose={handleClose}
        slots={{ transition: ModalTransition }}
        slotProps={{
          paper: {
            component: "form",
            "data-testid": "create-announcement-modal",
            onSubmit: handleSubmit(handleFormSubmit),
            sx: { borderRadius: "24px" },
          } as any,
        }}
      >
        <StyledDialogTitle
          title={
            selectedAnnouncement ? "Edit Announcement" : "Create Announcement"
          }
          testIdPrefix="create-announcement-modal"
          handleClose={handleClose}
          actionType="close"
        />

        <DialogContent>
          <Box
            sx={{
              width: { mobile: "250px", tablet: "600px", laptop: "800px" },
              padding: { mobile: "10px", laptop: "20px" },
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Typography fontSize={14} color="#6D87AA">
              Announcement Banner
            </Typography>
            <Controller
              name="announcementPhoto"
              control={control}
              render={({ field }) => (
                <Box
                  data-testid="create-announcement-banner-dropzone"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleDrop}
                  sx={{
                    width: "100%",
                    aspectRatio: 4,
                    border: "2px dashed #92A1B2",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    backgroundColor: "#F2F5FB",
                    position: "relative",
                  }}
                >
                  <img
                    src={previewUrl ?? "/default-banner-image.png"}
                    alt="Banner"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "fill",
                      pointerEvents: "none",
                    }}
                  />

                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      zIndex: 2,
                      backgroundColor: "#F2F5FB",
                      padding: "6px 12px",
                      borderRadius: "12px",
                      border: "1px solid #92A1B2",
                      fontSize: "12px",
                      color: "#123257",
                      fontWeight: 500,
                      cursor: "pointer",
                    }}
                    onClick={handleAddClick}
                  >
                    {previewUrl ? "Replace Image" : "Choose An Image"}
                    <input
                      ref={fileInputRef}
                      type="file"
                      data-testid="create-announcement-banner-file-input"
                      accept="image/jpeg,image/jpg,image/png"
                      style={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        opacity: 0,
                        cursor: "pointer",
                      }}
                      onChange={(e) => {
                        handleFileChange(e);
                        field.onChange(e.target.files?.[0]);
                      }}
                    />
                  </Box>
                </Box>
              )}
            />

            <Box
              sx={{
                display: "flex",
                gap: 2,
                pt: "5px",
                flexDirection: { mobile: "column", tablet: "row" },
              }}
            >
              <Controller
                name="date"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    label="Date"
                    value={field.value ? dayjs(field.value) : null}
                    onChange={(date) => {
                      if (date && date.isValid()) {
                        field.onChange(date.toISOString());
                      } else {
                        field.onChange(undefined);
                      }
                    }}
                    {...(() => {
                      const dp = datePickerProps(errors);
                      return {
                        ...dp,
                        slotProps: {
                          ...dp.slotProps,
                          textField: {
                            ...dp.slotProps?.textField,
                            inputProps: { "data-testid": "create-announcement-date-textfield" },
                          },
                        },
                      };
                    })()}
                  />
                )}
              />
              <Controller
                name="category"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    select
                    label="Category Please Select"
                    sx={commonTextFieldProps}
                    value={field.value ?? ""}
                    onChange={(e) => {
                      const num = Number(e.target.value);
                      field.onChange(isNaN(num) ? undefined : num);
                    }}
                    slotProps={{
                      select: {
                        SelectDisplayProps: { "data-testid": "create-announcement-category-select" } as any,
                        renderValue: (selected) => {
                          const num = Number(selected);
                          const found = announcementCategoryOptions.find(
                            (opt) => opt.value === num,
                          );
                          return found ? found.label : "";
                        },
                        MenuProps: {
                          PaperProps: {
                            sx: (theme: Theme) => ({
                              border: `1px solid ${theme.palette.primary.main}`,
                              borderRadius: "8px",
                              boxShadow: 3,
                              maxHeight: 200,
                            }),
                          },
                        },
                      },
                    }}
                  >
                    {announcementCategoryOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Typography color="primary.main">
                          {option.label}
                        </Typography>
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Box>

            <Controller
              name="title"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  sx={commonTextFieldProps}
                  variant="outlined"
                  label="Title"
                  inputProps={{ "data-testid": "create-announcement-title-textfield" }}
                  error={!!errors.title}
                  helperText={errors.title?.message}
                />
              )}
            />

            <Typography fontSize={14} color="#6D87AA">
              Body
            </Typography>
            <GlobalStyles
              styles={(theme) => ({
                ".ql-toolbar": {
                  border: `1px solid ${theme.palette.text.secondary} !important`,
                  borderRadius: "6px 6px 0 0 !important",
                },

                ".ql-container": {
                  border: `1px solid ${theme.palette.text.secondary} !important`,

                  borderTop: "none !important",
                  borderRadius: "0 0 6px 6px !important",
                },

                ".ql-toolbar:hover": {
                  borderColor: "#123257 !important",
                },
                ".ql-container:hover": {
                  borderColor: "#123257 !important",
                },

                ".ql-formats button": {
                  position: "relative",
                },

                ".ql-formats button:hover::after, .ql-formats button:focus::after, .ql-formats button:active::after":
                  {
                    position: "absolute",
                    background: "#0d1e42",
                    color: "#fff",
                    padding: "0.5em",
                    borderRadius: "0.4em",
                    fontSize: "12px",
                    whiteSpace: "nowrap",
                    zIndex: 999999,
                    top: "-120%",
                    left: "50%",
                    transform: "translateX(-50%)",
                  },

                ".ql-bold:hover::after, .ql-bold:active::after, .ql-bold:focus::after":
                  {
                    content: `"Bold"`,
                  },
                ".ql-italic:hover::after, .ql-italic:active::after, .ql-italic:focus::after":
                  {
                    content: `"Italic"`,
                  },
                ".ql-underline:hover::after, .ql-underline:active::after, .ql-underline:focus::after":
                  {
                    content: `"Underlined"`,
                  },
                ".ql-picker.ql-size:not(.ql-expanded) .ql-picker-label:hover::after":
                  {
                    content: `"Font Size"`,
                    position: "absolute",
                    top: "-150%",
                    left: "50%",
                    transform: "translateX(-50%)",
                    background: "#0d1e42",
                    color: "#fff",
                    padding: "0.5em",
                    borderRadius: "0.4em",
                    fontSize: "12px",
                    whiteSpace: "nowrap",
                    zIndex: 999999,
                  },
                ".ql-picker.ql-background .ql-picker-label:hover::after": {
                  content: `"Text Highlight"`,
                  position: "absolute",
                  top: "-150%",
                  left: "50%",
                  transform: "translateX(-50%)",
                  background: "#0d1e42",
                  color: "#fff",
                  padding: "0.5em",
                  borderRadius: "0.4em",
                  fontSize: "12px",
                  whiteSpace: "nowrap",
                  zIndex: 999999,
                },

                ".ql-picker.ql-color .ql-picker-label:hover::after": {
                  content: `"Font Color"`,
                  position: "absolute",
                  top: "-150%",
                  left: "50%",
                  transform: "translateX(-50%)",
                  background: "#0d1e42",
                  color: "#fff",
                  padding: "0.5em",
                  borderRadius: "0.4em",
                  fontSize: "12px",
                  whiteSpace: "nowrap",
                  zIndex: 999999,
                },
                ".ql-picker.ql-align .ql-picker-label:hover::after": {
                  content: `"Text Alignment"`,
                  position: "absolute",
                  top: "-150%",
                  left: "50%",
                  transform: "translateX(-50%)",
                  background: "#0d1e42",
                  color: "#fff",
                  padding: "0.5em",
                  borderRadius: "0.4em",
                  fontSize: "12px",
                  whiteSpace: "nowrap",
                  zIndex: 999999,
                },

                '.ql-list[value="bullet"]:hover::after, .ql-list[value="bullet"]:active::after, .ql-list[value="bullet"]:focus::after':
                  {
                    content: `"Unordered List"`,
                  },
                '.ql-list[value="ordered"]:hover::after, .ql-list[value="ordered"]:active::after, .ql-list[value="ordered"]:focus::after':
                  {
                    content: `"Ordered List"`,
                  },
                ".ql-container ol, .ql-container ul": {
                  marginBlockStart: 0,
                },

                '.ql-snow .ql-picker.ql-color.ql-expanded .ql-picker-options .ql-picker-item[data-value]:not([data-value=""]), \
                .ql-snow .ql-picker.ql-background.ql-expanded .ql-picker-options .ql-picker-item[data-value]:not([data-value=""])':
                  {
                    borderRadius: "4px",
                  },
                ".ql-snow .ql-picker.ql-color.ql-expanded .ql-picker-options, .ql-snow .ql-picker.ql-background.ql-expanded .ql-picker-options":
                  {
                    display: "grid",
                    gridTemplateColumns: "repeat(6, 20px)",
                    gridTemplateRows: "auto repeat(auto-fill, 120x)",
                    gridGap: "3px",
                    padding: "8px",
                  },

                '.ql-snow .ql-picker.ql-color.ql-expanded .ql-picker-options .ql-picker-item[data-value=""]':
                  {
                    gridColumn: "1 / -1",
                    gridRow: "1",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                    color: "rgba(0,0,0,0.7)",
                    background: "none",
                    cursor: "pointer",
                    userSelect: "none",
                    outline: "none !important",
                    boxShadow: "none !important",
                    border: "none !important",
                  },
                '.ql-snow .ql-picker.ql-color.ql-expanded .ql-picker-options .ql-picker-item[data-value=""]::before':
                  {
                    content: `"✖ Remove Color"`,
                    position: "absolute",
                    top: "8px",
                    left: "8px",
                    right: "8px",
                    textAlign: "left",
                  },

                '.ql-snow .ql-picker.ql-background.ql-expanded .ql-picker-options .ql-picker-item[data-value=""]':
                  {
                    gridColumn: "1 / -1",
                    gridRow: "1",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                    color: "rgba(0,0,0,0.7)",
                    background: "none",
                    cursor: "pointer",
                    userSelect: "none",
                    outline: "none !important",
                    boxShadow: "none !important",
                    border: "none !important",
                  },
                '.ql-snow .ql-picker.ql-background.ql-expanded .ql-picker-options .ql-picker-item[data-value=""]::before':
                  {
                    content: `"✖ Remove Color"`,
                    position: "absolute",
                    top: "8px",
                    left: "8px",
                    right: "8px",
                    textAlign: "left",
                  },
              })}
            />

            <Controller
              name="body"
              control={control}
              rules={{ required: "Body is required" }}
              render={({ field }) => (
                <div style={{ marginBottom: 16 }} data-testid="create-announcement-body-editor">
                  <ReactQuill
                    theme="snow"
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Enter text here..."
                    modules={{
                      toolbar: [
                        ["bold", "italic", "underline"],
                        [{ size: ["small", false, "large", "huge"] }],
                        [{ background: BG_OPTIONS }, { color: COLOR_OPTIONS }],

                        [{ align: [] }],

                        [{ list: "ordered" }, { list: "bullet" }],
                      ],
                    }}
                    formats={[
                      "bold",
                      "italic",
                      "underline",
                      "size",
                      "background",
                      "color",
                      "align",
                      "list",
                    ]}
                  />
                  {errors.body && (
                    <Typography color="error" variant="caption">
                      {errors.body.message}
                    </Typography>
                  )}
                </div>
              )}
            />
          </Box>
        </DialogContent>

        <StyledDialogActions>
          <Button
            id="create-announcement-cancel-button"
            data-testid="create-announcement-cancel-button"
            onClick={handleClose}
            disabled={isCreateLoading || isUpdateLoading}
          >
            Cancel
          </Button>
          <Button
            id="create-announcement-save-button"
            data-testid="create-announcement-save-button"
            type="submit"
            disabled={
              selectedAnnouncement
                ? !anncouncementPrivileges.update
                : !anncouncementPrivileges.create ||
                  isCreateLoading ||
                  isUpdateLoading
            }
            sx={commonButtonProps}
            variant="contained"
          >
            {isCreateLoading || isUpdateLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : selectedAnnouncement ? (
              "Save Changes"
            ) : (
              "Save"
            )}
          </Button>
        </StyledDialogActions>
        {openCropDialog && (
          <ImageCropDialog
            open={openCropDialog}
            onClose={() => setOpenCropDialog(false)}
            originalFileName={originalFileName}
            imageUrl={previewUrl2}
            onSave={(croppedFile) => {
              const newPreviewUrl = URL.createObjectURL(croppedFile);
              setPreviewUrl(newPreviewUrl);
              setOpenCropDialog(false);
              setOriginalFileName(originalFileName);
              setValue("announcementPhoto", croppedFile);
            }}
          />
        )}
      </Dialog>
    </LocalizationProvider>
  );
}
